import { z } from 'zod';
import {
  FieldKeySchema,
  IdSchema,
  IsoDateTimeSchema,
  ScalarValueSchema,
  SchemaVersionSchema,
  TimeGrainSchema,
} from './common.js';
import {
  ComparisonSchema,
  FilterOperatorSchema,
  FilterSchema,
  QueryLimitSchema,
} from './analysis-state.js';
import {
  CalculatedColumnSchema,
  CalculatedDimensionSchema,
  CalculatedMeasureSchema,
  DimensionSchema,
  MeasureSchema,
} from './semantic-model.js';
import { EncodingChannelSchema, VisualizationTypeSchema } from './visualization.js';

/**
 * AI Action JSON: unico canal por el que la IA puede influir en un analisis.
 * La IA no genera SQL, codigo ni componentes: propone acciones tipadas de un catalogo
 * cerrado que la plataforma valida contra el Semantic Model antes de aplicarlas.
 *
 * Todos los objetos son estrictos: cualquier propiedad no declarada invalida la accion.
 */
export const AIActionMetadataSchema = z.strictObject({
  /** Confianza del modelo de IA entre 0 y 1. */
  confidence: z.number().min(0).max(1).optional(),
  rationale: z.string().max(2000).optional(),
});
export type AIActionMetadata = z.infer<typeof AIActionMetadataSchema>;

const action = <const T extends string, P extends z.ZodType>(type: T, payload: P) =>
  z.strictObject({
    id: IdSchema,
    type: z.literal(type),
    payload,
    metadata: AIActionMetadataSchema.optional(),
  });

const empty = z.strictObject({});

export const SortTargetSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('dimension'), dimensionId: IdSchema }),
  z.strictObject({ kind: z.literal('measure'), measureId: IdSchema }),
]);
export type SortTarget = z.infer<typeof SortTargetSchema>;

/** Aspectos del analisis que la plataforma sabe explicar (0.2.2). */
export const AI_EXPLANATION_TOPICS = ['result', 'filters', 'fields', 'measure'] as const;
export const AIExplanationTopicSchema = z.enum(AI_EXPLANATION_TOPICS);
export type AIExplanationTopic = z.infer<typeof AIExplanationTopicSchema>;

export const AIActionSchema = z.discriminatedUnion('type', [
  action(
    'addDimension',
    z.strictObject({
      dimensionId: IdSchema,
      key: FieldKeySchema.optional(),
      timeGrain: TimeGrainSchema.optional(),
    }),
  ),
  action('removeDimension', z.strictObject({ dimensionId: IdSchema })),
  action('addMeasure', z.strictObject({ measureId: IdSchema, key: FieldKeySchema.optional() })),
  action('removeMeasure', z.strictObject({ measureId: IdSchema })),
  action('addFilter', z.strictObject({ filter: FilterSchema })),
  action('removeFilter', z.strictObject({ filterId: IdSchema })),
  action('clearFilters', empty),
  action(
    'sort',
    z.strictObject({
      by: z
        .array(z.strictObject({ target: SortTargetSchema, direction: z.enum(['asc', 'desc']) }))
        .min(1),
    }),
  ),
  action('setLimit', z.strictObject({ limit: QueryLimitSchema.nullable() })),
  action(
    'setVisualization',
    z.strictObject({
      type: VisualizationTypeSchema,
      encodings: z.partialRecord(EncodingChannelSchema, z.array(FieldKeySchema)).optional(),
    }),
  ),
  action(
    'drillDown',
    z.strictObject({
      hierarchyId: IdSchema,
      value: ScalarValueSchema,
      /** Nivel destino; si se omite, el siguiente nivel de la jerarquia. */
      levelId: IdSchema.optional(),
    }),
  ),
  action('drillUp', z.strictObject({ hierarchyId: IdSchema })),
  action(
    'createMeasure',
    z.strictObject({ measure: z.union([MeasureSchema, CalculatedMeasureSchema]) }),
  ),
  action(
    'createDimension',
    z.strictObject({ dimension: z.union([DimensionSchema, CalculatedDimensionSchema]) }),
  ),
  action('createCalculatedColumn', z.strictObject({ column: CalculatedColumnSchema })),
  action('comparePeriods', z.strictObject({ comparison: ComparisonSchema })),
  action('resetAnalysis', empty),
  /** Narrativa o insight textual; no modifica el estado. */
  action('narrative', z.strictObject({ text: z.string().min(1).max(8000) })),
  /** 0.2.2: modifica operador y/o valores de un filtro existente sin cambiar su destino. */
  action(
    'updateFilter',
    z.strictObject({
      filterId: IdSchema,
      operator: FilterOperatorSchema.optional(),
      values: z.array(ScalarValueSchema).optional(),
    }),
  ),
  /** 0.2.2: totales y subtotales; solo cambia las propiedades indicadas. */
  action(
    'updateGrouping',
    z.strictObject({
      grandTotal: z.boolean().optional(),
      subtotals: z.boolean().optional(),
      rollupKeys: z.array(FieldKeySchema).optional(),
    }),
  ),
  /** 0.2.2: limpia las selecciones asociativas (todas o las de una dimension). */
  action('resetSelections', z.strictObject({ dimensionId: IdSchema.optional() })),
  /** 0.2.2: solicita una explicacion determinista; no modifica el estado. */
  action(
    'requestExplanation',
    z.strictObject({ topic: AIExplanationTopicSchema, measureId: IdSchema.optional() }),
  ),
  /** 0.2.2: sugiere una visualizacion; nunca se aplica sin una accion explicita del usuario. */
  action('suggestVisualization', z.strictObject({ type: VisualizationTypeSchema })),
]);
export type AIAction = z.infer<typeof AIActionSchema>;
export type AIActionInput = z.input<typeof AIActionSchema>;
export type AIActionType = AIAction['type'];

export const AI_ACTION_TYPES = [
  'addDimension',
  'removeDimension',
  'addMeasure',
  'removeMeasure',
  'addFilter',
  'removeFilter',
  'clearFilters',
  'sort',
  'setLimit',
  'setVisualization',
  'drillDown',
  'drillUp',
  'createMeasure',
  'createDimension',
  'createCalculatedColumn',
  'comparePeriods',
  'resetAnalysis',
  'narrative',
  'updateFilter',
  'updateGrouping',
  'resetSelections',
  'requestExplanation',
  'suggestVisualization',
] as const satisfies readonly AIActionType[];

/** Acciones informativas: no modifican el Analysis State ni el Semantic Model. */
export const AI_INFORMATIONAL_ACTION_TYPES = [
  'narrative',
  'requestExplanation',
  'suggestVisualization',
] as const satisfies readonly AIActionType[];

/** Acciones que proponen cambios al Semantic Model: requieren confirmacion explicita. */
export const AI_MODEL_ACTION_TYPES = [
  'createMeasure',
  'createDimension',
  'createCalculatedColumn',
] as const satisfies readonly AIActionType[];

export const AIActionEnvelopeSchema = z.strictObject({
  schemaVersion: SchemaVersionSchema,
  id: IdSchema,
  createdAt: IsoDateTimeSchema,
  /** Analisis sobre el que se proponen las acciones. */
  analysisStateId: IdSchema,
  semanticModelId: IdSchema,
  source: z.strictObject({
    providerId: IdSchema,
    model: z.string().min(1).max(200).optional(),
  }),
  /** Explicacion breve para el usuario de por que se proponen las acciones. */
  rationale: z.string().max(4000).optional(),
  actions: z.array(AIActionSchema).min(1).max(50),
});
export type AIActionEnvelope = z.infer<typeof AIActionEnvelopeSchema>;
export type AIActionEnvelopeInput = z.input<typeof AIActionEnvelopeSchema>;
